import { useState } from 'react';
import { useToast } from '@/components/base/ToastProvider';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

const shareChannels = [
  { key: 'wechat', label: '微信', icon: 'ri-wechat-line' },
  { key: 'weibo', label: '微博', icon: 'ri-weibo-line' },
  { key: 'qq', label: 'QQ', icon: 'ri-qq-line' },
  { key: 'link', label: '复制链接', icon: 'ri-link' },
];

export default function ShareDialog({ open, onClose, title }: ShareDialogProps) {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const currentUrl = window.location.href;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
    } catch {
      // clipboard may be unavailable; still show feedback for the demo
    }
    setCopied(true);
    showToast('链接已复制');
    window.setTimeout(() => setCopied(false), 1800);
  };

  const handleChannel = (key: string) => {
    if (key === 'link') {
      handleCopy();
      return;
    }
    showToast('演示环境：分享功能仅作提示');
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/50 px-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-lg bg-background-50 p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-1 flex items-start justify-between gap-4">
          <h3 className="font-heading text-lg font-semibold text-foreground-950">
            分享
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
            aria-label="关闭"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <p className="mb-5 line-clamp-1 text-sm text-foreground-500">{title}</p>

        <div className="grid grid-cols-4 gap-3">
          {shareChannels.map((channel) => (
            <button
              key={channel.key}
              type="button"
              onClick={() => handleChannel(channel.key)}
              className="flex cursor-pointer flex-col items-center gap-2 rounded-md px-1 py-2 transition-colors hover:bg-background-200"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className={`${channel.icon} text-xl`}></i>
              </span>
              <span className="whitespace-nowrap text-xs text-foreground-700">
                {channel.label}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-3 py-2">
          <i className="ri-link text-foreground-400"></i>
          <span className="line-clamp-1 flex-1 text-xs text-foreground-600">
            {currentUrl}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            {copied ? '已复制' : '复制'}
          </button>
        </div>
      </div>
    </div>
  );
}